import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, 
  LogOut, 
  MapPin,
  Navigation,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Play,
  Users,
  RefreshCw,
  ExternalLink
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const GouvernantMap = () => {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [geocodedTasks, setGeocodedTasks] = useState([]);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);

  const geocodeAddresses = useCallback(async (tasksData) => {
    const geocoded = [];
    
    for (const task of tasksData) {
      if (task.property_address) {
        try {
          // Use French government geocoding API
          const searchQuery = encodeURIComponent(
            `${task.property_address} ${task.property_postcode || ''} ${task.property_city || ''}`
          );
          const response = await fetch(
            `https://api-adresse.data.gouv.fr/search/?q=${searchQuery}&limit=1`
          );
          const data = await response.json();
          
          if (data.features && data.features.length > 0) {
            const [lng, lat] = data.features[0].geometry.coordinates;
            geocoded.push({
              ...task,
              lat,
              lng
            });
          } else {
            // Fallback: try with just city
            if (task.property_city) {
              const cityResponse = await fetch(
                `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(task.property_city)}&type=municipality&limit=1`
              );
              const cityData = await cityResponse.json();
              if (cityData.features && cityData.features.length > 0) {
                const [lng, lat] = cityData.features[0].geometry.coordinates;
                geocoded.push({
                  ...task,
                  lat,
                  lng,
                  approximateLocation: true
                });
              }
            }
          }
        } catch (err) {
          console.error('Geocoding error for', task.property_name, err);
        }
      }
    }
    
    setGeocodedTasks(geocoded);
  }, []);

  const fetchTodayTasks = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/gouvernant/tasks-today`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTasks(data);
        // Geocode addresses
        geocodeAddresses(data);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  }, [token, geocodeAddresses]);

  useEffect(() => {
    fetchTodayTasks();
  }, [fetchTodayTasks]);

  useEffect(() => {
    if (!mapRef.current || geocodedTasks.length === 0) return;

    // Initialize map if not already done
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = L.map(mapRef.current).setView([48.0, -3.0], 9); // Bretagne center
      
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors'
      }).addTo(mapInstanceRef.current);
    }

    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // Add markers for each task
    const bounds = L.latLngBounds();
    
    geocodedTasks.forEach(task => {
      const color = task.property_color || '#003366';
      const statusIcon = getStatusIconSvg(task.status);
      
      // Create custom icon
      const customIcon = L.divIcon({
        className: 'custom-marker',
        html: `
          <div style="
            background-color: ${color};
            width: 36px;
            height: 36px;
            border-radius: 50%;
            border: 3px solid white;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-size: 14px;
          ">
            ${statusIcon}
          </div>
        `,
        iconSize: [36, 36],
        iconAnchor: [18, 18]
      });

      const marker = L.marker([task.lat, task.lng], { icon: customIcon })
        .addTo(mapInstanceRef.current)
        .bindPopup(`
          <div style="min-width: 200px;">
            <h3 style="font-weight: bold; margin-bottom: 8px; color: ${color};">
              ${task.property_name}
            </h3>
            <p style="margin: 4px 0; color: #666;">
              <strong>Ville:</strong> ${task.property_city || 'N/A'}
            </p>
            <p style="margin: 4px 0; color: #666;">
              <strong>FDM:</strong> ${task.housekeeper_names?.join(' & ') || 'Non assignée'}
            </p>
            <p style="margin: 4px 0;">
              <strong>Statut:</strong> 
              <span style="
                background-color: ${getStatusColor(task.status)};
                color: white;
                padding: 2px 8px;
                border-radius: 12px;
                font-size: 12px;
              ">
                ${getStatusLabel(task.status)}
              </span>
            </p>
            ${task.time_spent_minutes ? `
              <p style="margin: 4px 0; color: #666;">
                <strong>Temps:</strong> ${task.time_spent_minutes} min
              </p>
            ` : ''}
          </div>
        `);
      
      marker.on('click', () => setSelectedTask(task));
      markersRef.current.push(marker);
      bounds.extend([task.lat, task.lng]);
    });

    // Fit map to markers
    if (geocodedTasks.length > 0) {
      mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50] });
    }

    return () => {
      // Cleanup
    };
  }, [geocodedTasks]);

  const getStatusIconSvg = (status) => {
    switch(status) {
      case 'termine': return '✓';
      case 'en_cours': return '▶';
      case 'probleme': return '!';
      default: return '○';
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'termine': return '#10b981';
      case 'en_cours': return '#f59e0b';
      case 'probleme': return '#ef4444';
      default: return '#3b82f6';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      affectee: 'Affectée',
      en_cours: 'En cours',
      termine: 'Terminée',
      probleme: 'Problème'
    };
    return labels[status] || status;
  };

  const getStatusBadge = (status) => {
    const styles = {
      affectee: 'bg-blue-100 text-blue-700',
      en_cours: 'bg-amber-100 text-amber-700',
      termine: 'bg-emerald-100 text-emerald-700',
      probleme: 'bg-red-100 text-red-700'
    };
    return <Badge className={`${styles[status] || 'bg-gray-100'} border-0`}>{getStatusLabel(status)}</Badge>;
  };

  const openInMaps = (task) => {
    const query = encodeURIComponent(`${task.property_address} ${task.property_city || ''}`);
    window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank');
  };

  const openInWaze = (task) => {
    if (task.lat && task.lng) {
      window.open(`https://waze.com/ul?ll=${task.lat},${task.lng}&navigate=yes`, '_blank');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-[#003366] text-white px-4 py-4 shadow-lg sticky top-0 z-[1000]">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              className="text-white hover:bg-white/20"
              onClick={() => navigate('/gouvernant')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-bold text-lg">Carte du jour</h1>
              <p className="text-blue-200 text-sm">
                {format(new Date(), "EEEE d MMMM", { locale: fr })} - {tasks.length} ménages
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon"
              className="text-white hover:bg-white/20"
              onClick={fetchTodayTasks}
            >
              <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button 
              variant="ghost" 
              className="text-white hover:bg-white/20"
              onClick={handleLogout}
            >
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4">
        {/* Stats bar */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          <div className="bg-white rounded-lg p-3 text-center shadow-sm">
            <p className="text-xl font-bold text-slate-800">{tasks.length}</p>
            <p className="text-xs text-slate-500">Total</p>
          </div>
          <div className="bg-emerald-50 rounded-lg p-3 text-center shadow-sm">
            <p className="text-xl font-bold text-emerald-600">
              {tasks.filter(t => t.status === 'termine').length}
            </p>
            <p className="text-xs text-emerald-600">Terminés</p>
          </div>
          <div className="bg-amber-50 rounded-lg p-3 text-center shadow-sm">
            <p className="text-xl font-bold text-amber-600">
              {tasks.filter(t => t.status === 'en_cours').length}
            </p>
            <p className="text-xs text-amber-600">En cours</p>
          </div>
          <div className="bg-blue-50 rounded-lg p-3 text-center shadow-sm">
            <p className="text-xl font-bold text-blue-600">
              {tasks.filter(t => t.status === 'affectee').length}
            </p>
            <p className="text-xs text-blue-600">À faire</p>
          </div>
        </div>

        {/* Map Container */}
        <Card className="border-0 shadow-lg mb-4 overflow-hidden">
          <div 
            ref={mapRef} 
            className="h-[400px] md:h-[500px] w-full"
            style={{ zIndex: 1 }}
          />
        </Card>

        {/* Task List Below Map */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="h-5 w-5 text-[#003366]" />
              Liste des ménages
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin h-8 w-8 border-4 border-[#003366] border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-slate-500">Chargement de la carte...</p>
              </div>
            ) : tasks.length === 0 ? (
              <p className="text-center py-8 text-slate-500">Aucun ménage prévu aujourd'hui</p>
            ) : (
              <div className="space-y-3">
                {tasks.map(task => (
                  <div 
                    key={task.id}
                    className={`p-4 rounded-xl border transition-all cursor-pointer ${
                      selectedTask?.id === task.id 
                        ? 'bg-blue-50 border-blue-300 shadow-md' 
                        : 'bg-slate-50 border-slate-100 hover:bg-slate-100'
                    }`}
                    onClick={() => {
                      setSelectedTask(task);
                      // Center map on this task if geocoded
                      const geocoded = geocodedTasks.find(g => g.id === task.id);
                      if (geocoded && mapInstanceRef.current) {
                        mapInstanceRef.current.setView([geocoded.lat, geocoded.lng], 14);
                      }
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: task.property_color || '#003366' }}
                          />
                          <span className="font-semibold text-slate-800">
                            {task.property_name}
                          </span>
                          {getStatusBadge(task.status)}
                        </div>
                        <p className="text-sm text-slate-500">{task.property_city}</p>
                        {task.housekeeper_names?.length > 0 && (
                          <div className="flex items-center gap-1 mt-2 text-sm text-slate-600">
                            <Users className="h-4 w-4" />
                            {task.housekeeper_names.join(' & ')}
                          </div>
                        )}
                        {task.time_spent_minutes > 0 && (
                          <div className="flex items-center gap-1 mt-1 text-sm text-slate-500">
                            <Clock className="h-4 w-4" />
                            {task.time_spent_minutes} min
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs"
                          onClick={(e) => { e.stopPropagation(); openInMaps(task); }}
                        >
                          <Navigation className="h-3 w-3 mr-1" />
                          Maps
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs"
                          onClick={(e) => { e.stopPropagation(); openInWaze(task); }}
                        >
                          <ExternalLink className="h-3 w-3 mr-1" />
                          Waze
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default GouvernantMap;
